// campaignDetail.js — analytics view for a single campaign
//
// Realtime funnel + per-language / per-city breakdown + failure log.
// Backend data sources (all live per MARKETING_CAMPAIGNS_PLAN §6):
//   • marketing_campaigns/{id}.stats     — funnel counters (sent, delivered, opened,
//     clicked, converted, failed, revenue). Written by dispatchPending in batches
//     and reconciled nightly by marketingStatsConsolidator.
//   • marketing_campaigns/{id}.audience  — resolve-time counts (estimated, resolved,
//     optedOut, noToken, suppressed).
//   • notifications where campaignId==X  — per-recipient docs. Used for the failure
//     log tail + a sampled per-language breakdown (funnel numbers themselves come
//     from stats to avoid pulling 50k+ docs client-side).
//
// See MARKETING_CAMPAIGNS_PLAN §5 Phase 6, §11.

import React, { useEffect, useState } from 'react';
import { Card, CardBody, Row, Col, Table, Button, Alert } from 'reactstrap';
import moment from 'moment';
import { firestore } from '../../../../helpers/Firebase';

// Colors align with STATUS_LABELS in campaignsList.js
const STATUS_COLORS = {
  draft:     '#6c757d',
  scheduled: '#17a2b8',
  running:   '#ffc107',
  completed: '#28a745',
  failed:    '#dc3545',
  canceled:  '#6c757d',
};

const pill = (bg, fg = '#fff') => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 10,
  background: bg,
  color: fg,
  fontSize: '0.75rem',
  fontWeight: 500,
  marginRight: 4,
});

// Rate as % with 1 decimal — 0 denominator returns 0 (not NaN or Infinity in the UI).
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);

// Firestore's "missing composite index" error includes a one-click create URL.
// Parse it out so we can render it as a clickable link on the page instead of
// silently logging to console. Returns null if no URL is present in the message.
const extractIndexCreateUrl = (message) => {
  if (!message) return null;
  const m = message.match(/https:\/\/console\.firebase\.google\.com\/[^\s"'`]+/);
  return m ? m[0] : null;
};

// Small reusable banner — takes a raw Firestore error, extracts the create-index
// URL if present, and renders it prominently so ops isn't hunting the console log.
const IndexErrorBanner = ({ message, context }) => {
  const url = extractIndexCreateUrl(message);
  return (
    <Alert color="warning" className="mb-3">
      <div><strong>{context}:</strong> composite index missing.</div>
      {url ? (
        <div className="mt-2">
          <a href={url} target="_blank" rel="noopener noreferrer">
            Create the index in Firebase console →
          </a>
          <div className="text-muted mt-1"><small>Building takes 5–15 min. Retry this page after.</small></div>
        </div>
      ) : (
        <div className="mt-2 small"><code>{message}</code></div>
      )}
    </Alert>
  );
};

// Sample cap for the per-language breakdown. Funnel counts come from campaign.stats;
// this sample is only used for splitting rates across languages, so 500 is a fair
// balance between accuracy and client-side memory for large campaigns.
const BREAKDOWN_SAMPLE_LIMIT = 500;
const FAILURE_LOG_LIMIT = 50;

const CampaignDetail = ({ match, history }) => {
  const id = match.params.id;
  const [campaign, setCampaign] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failedNotifs, setFailedNotifs] = useState([]);
  const [failedNotifsError, setFailedNotifsError] = useState(null);
  const [breakdownSample, setBreakdownSample] = useState(null);
  const [sampleError, setSampleError] = useState(null);

  // Realtime campaign subscription — updates the funnel counts live while
  // dispatchPending is still writing stats deltas.
  useEffect(() => {
    if (!id) return;
    const unsub = firestore().collection('marketing_campaigns').doc(id).onSnapshot(
      (snap) => {
        if (!snap.exists) { setNotFound(true); setLoading(false); return; }
        setCampaign({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => { console.error('campaign detail listener error', err); setLoading(false); },
    );
    return () => unsub();
  }, [id]);

  // Failure log tail — one-shot, most recent failed dispatches.
  useEffect(() => {
    if (!id) return;
    firestore().collection('notifications')
      .where('campaignId', '==', id)
      .where('status', '==', 'failed')
      .orderBy('failedAt', 'desc')
      .limit(FAILURE_LOG_LIMIT)
      .get()
      .then((snap) => setFailedNotifs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => {
        // Missing composite index is the common failure here. Show the raw
        // Firestore error (which carries a create-index URL) so ops can create
        // it from the page rather than digging in the console.
        setFailedNotifsError(err.message);
      });
  }, [id]);

  // Per-language breakdown from a sample. Not used to compute the funnel — that
  // comes from stats — only to show open/click rate splits.
  useEffect(() => {
    if (!id) return;
    firestore().collection('notifications')
      .where('campaignId', '==', id)
      .limit(BREAKDOWN_SAMPLE_LIMIT)
      .get()
      .then((snap) => {
        const rows = snap.docs.map((d) => d.data());
        const byLang = {};
        for (const r of rows) {
          const l = r.language || 'unknown';
          if (!byLang[l]) byLang[l] = { sent: 0, delivered: 0, opened: 0, clicked: 0, converted: 0 };
          if (r.status !== 'skipped' && r.status !== 'pending') byLang[l].sent += 1;
          if (r.deliveredAt) byLang[l].delivered += 1;
          if (r.openedAt) byLang[l].opened += 1;
          if (r.clickedAt) byLang[l].clicked += 1;
          if (r.convertedAt) byLang[l].converted += 1;
        }
        setBreakdownSample({ byLang, sampleSize: rows.length });
      })
      .catch((err) => setSampleError(err.message));
  }, [id]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  if (notFound) {
    return (
      <div style={{ padding: 24 }}>
        <p>Campaign not found.</p>
        <Button size="sm" onClick={() => history.push('/app/marketing/campaigns')}>Back to list</Button>
      </div>
    );
  }
  if (!campaign) return null;

  const stats = campaign.stats || {};
  const audience = campaign.audience || {};
  const resolved = audience.resolved || 0;
  const sent = stats.sent || 0;
  const delivered = stats.delivered || 0;
  const opened = stats.opened || 0;
  const clicked = stats.clicked || 0;
  const converted = stats.converted || 0;
  const failed = stats.failed || 0;
  const revenue = stats.revenue || 0;

  // Funnel stages in order. Each stage's percentage is relative to the PREVIOUS stage
  // (not to resolved) — this is the standard funnel semantic marketing expects.
  // Bar widths are relative to `resolved` so the visual funnel shows the classic taper.
  const stages = [
    { key: 'resolved',  label: 'Resolved',  count: resolved,  vsPrev: null },
    { key: 'sent',      label: 'Sent',      count: sent,      vsPrev: pct(sent, resolved) },
    { key: 'delivered', label: 'Delivered', count: delivered, vsPrev: pct(delivered, sent) },
    { key: 'opened',    label: 'Opened',    count: opened,    vsPrev: pct(opened, delivered) },
    { key: 'clicked',   label: 'Clicked',   count: clicked,   vsPrev: pct(clicked, opened) },
    { key: 'converted', label: 'Converted', count: converted, vsPrev: pct(converted, clicked) },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div className="d-flex align-items-center mb-3">
        <Button size="sm" outline color="secondary" onClick={() => history.push('/app/marketing/campaigns')} className="mr-3">
          ← Back
        </Button>
        <h4 className="mb-0 mr-3">{campaign.name || 'Untitled campaign'}</h4>
        <span style={pill(STATUS_COLORS[campaign.status] || '#6c757d')}>{campaign.status || 'draft'}</span>
        <Button size="sm" outline color="primary" className="ml-auto"
          onClick={() => history.push(`/app/marketing/campaigns/${id}/edit`)}>
          Edit campaign
        </Button>
      </div>

      {campaign.description && (
        <div className="text-muted mb-3"><small>{campaign.description}</small></div>
      )}

      <Row>
        <Col md="8">
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Funnel</h6>
              <div className="text-muted small mb-3">
                Counts from <code>campaign.stats</code> — updated live during dispatch,
                reconciled nightly by <code>marketingStatsConsolidator</code>.
              </div>
              {stages.map((s, i) => (
                <div key={s.key} className="mb-3">
                  <div className="d-flex justify-content-between align-items-baseline">
                    <span style={{ fontWeight: 500 }}>{s.label}</span>
                    <span>
                      <strong>{s.count.toLocaleString()}</strong>
                      {i > 0 && (
                        <span className="text-muted ml-2">
                          <small>{s.vsPrev}% of {stages[i - 1].label.toLowerCase()}</small>
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ background: '#e9ecef', height: 8, borderRadius: 4, marginTop: 4 }}>
                    <div style={{
                      background: '#3498db',
                      height: '100%',
                      width: `${Math.min(100, resolved > 0 ? (s.count / resolved) * 100 : 0)}%`,
                      borderRadius: 4,
                      transition: 'width 300ms',
                    }} />
                  </div>
                </div>
              ))}
              {failed > 0 && (
                <div className="mt-2 text-danger small">
                  <strong>{failed}</strong> failed to deliver — see failure log below
                </div>
              )}
            </CardBody>
          </Card>

          {breakdownSample && Object.keys(breakdownSample.byLang).length > 0 && (
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-1">
                  By language{' '}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    <small>
                      (sample of {breakdownSample.sampleSize.toLocaleString()} recipients
                      {breakdownSample.sampleSize >= BREAKDOWN_SAMPLE_LIMIT ? ' — capped' : ''})
                    </small>
                  </span>
                </h6>
                <Table size="sm" borderless className="mt-2">
                  <thead>
                    <tr>
                      <th>Language</th>
                      <th className="text-right">Sent</th>
                      <th className="text-right">Delivered</th>
                      <th className="text-right">Opened</th>
                      <th className="text-right">Clicked</th>
                      <th className="text-right">Converted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(breakdownSample.byLang).map(([lang, r]) => (
                      <tr key={lang}>
                        <td><strong>{lang}</strong></td>
                        <td className="text-right">{r.sent}</td>
                        <td className="text-right">
                          {r.delivered}{' '}
                          <span className="text-muted"><small>({pct(r.delivered, r.sent)}%)</small></span>
                        </td>
                        <td className="text-right">
                          {r.opened}{' '}
                          <span className="text-muted"><small>({pct(r.opened, r.delivered)}%)</small></span>
                        </td>
                        <td className="text-right">
                          {r.clicked}{' '}
                          <span className="text-muted"><small>({pct(r.clicked, r.opened)}%)</small></span>
                        </td>
                        <td className="text-right">
                          {r.converted}{' '}
                          <span className="text-muted"><small>({pct(r.converted, r.clicked)}%)</small></span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {sampleError && (
            <IndexErrorBanner message={sampleError} context="Per-language breakdown" />
          )}

          {failedNotifsError && (
            <IndexErrorBanner message={failedNotifsError} context="Failure log" />
          )}

          {failedNotifs.length > 0 && (
            <Card className="mb-3">
              <CardBody>
                <h6>
                  Recent failures{' '}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    <small>({failedNotifs.length}{failedNotifs.length >= FAILURE_LOG_LIMIT ? '+' : ''})</small>
                  </span>
                </h6>
                <Table size="sm" striped>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedNotifs.map((n) => (
                      <tr key={n.id}>
                        <td><small>{n.failedAt ? moment(n.failedAt).format('DD/MM HH:mm') : '—'}</small></td>
                        <td><small><code>{(n.user || '').substring(0, 12) || '—'}…</code></small></td>
                        <td><small className="text-danger">{n.error || 'unknown'}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}
        </Col>

        <Col md="4">
          <Card className="mb-3">
            <CardBody>
              <h6>Revenue attributed</h6>
              <div style={{ fontSize: '2rem', fontWeight: 600 }}>
                {revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                <small className="text-muted">AED</small>
              </div>
              {converted > 0 && (
                <div className="text-muted small">
                  Avg per conversion: {(revenue / converted).toFixed(2)} AED
                </div>
              )}
              {converted === 0 && sent > 0 && (
                <div className="text-muted small">No conversions attributed yet.</div>
              )}
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6>Audience</h6>
              <div className="mb-1"><small>Estimated:</small>{' '}<strong>{(audience.estimated || 0).toLocaleString()}</strong></div>
              <div className="mb-1"><small>Resolved:</small>{' '}<strong>{(audience.resolved || 0).toLocaleString()}</strong></div>
              {(audience.optedOut > 0) && <div className="mb-1"><small>Opted out:</small> {audience.optedOut}</div>}
              {(audience.noToken > 0) && <div className="mb-1"><small>No token:</small> {audience.noToken}</div>}
              {(audience.suppressed > 0) && <div className="mb-1"><small>Suppressed:</small> {audience.suppressed}</div>}
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6>Timing</h6>
              {campaign.schedule?.sendAt && (
                <div className="mb-1"><small>Scheduled:</small> {moment(campaign.schedule.sendAt).format('DD/MM/YYYY HH:mm')}</div>
              )}
              {campaign.launchedAt && (
                <div className="mb-1"><small>Launched:</small> {moment(campaign.launchedAt).format('DD/MM/YYYY HH:mm')}</div>
              )}
              {campaign.completedAt && (
                <div className="mb-1"><small>Completed:</small> {moment(campaign.completedAt).format('DD/MM/YYYY HH:mm')}</div>
              )}
              {campaign.launchedAt && campaign.completedAt && (
                <div className="mb-1">
                  <small>Duration:</small>{' '}
                  {moment.duration(campaign.completedAt - campaign.launchedAt).humanize()}
                </div>
              )}
              {!campaign.launchedAt && !campaign.schedule?.sendAt && (
                <div className="text-muted small">Not yet scheduled or sent.</div>
              )}
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6>Segment</h6>
              {campaign.segmentName ? (
                <>
                  <div>{campaign.segmentName}</div>
                  {campaign.segmentId && (
                    <Button size="sm" outline color="primary" className="mt-2"
                      onClick={() => history.push(`/app/marketing/segments/${campaign.segmentId}/edit`)}>
                      View segment
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-muted small">
                  Ad-hoc segment ({(campaign.segmentSnapshot?.rules || []).length} rules)
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6>Channels</h6>
              {(campaign.channels || []).map((c) => (
                <span key={c} style={pill('#e9ecef', '#495057')}>{c}</span>
              ))}
              {(!campaign.channels || campaign.channels.length === 0) && (
                <div className="text-muted small">No channels selected.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CampaignDetail;
